import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ErrorNote, FormActions, FormGroup, FormPanel, OkNote, Table, errorMessage } from '@/components/ui';
import { IMPORT_COLUMNS, importTemplate, readImportSheet, type ImportColumn, type ImportSheet } from './employeeCsv';

const MAX_ROWS = 2000;

/** What goes in each column, in plain words. */
const COLUMN_HELP: Record<ImportColumn, string> = {
  nameEn: 'Name in English (needed)',
  nameUr: 'Name in Urdu',
  fatherName: "Father's or husband's name",
  cnic: 'CNIC, like 35202-1234567-1',
  contactNo: 'Mobile number',
  farmCode: 'Farm code, like ABD-1. Empty for head office',
  department: 'Department name. A new one is added if it does not exist',
  designation: 'Designation name. A new one is added if it does not exist',
  workerType: 'MONTHLY or DAILY (needed)',
  payFrequency: 'MONTHLY, FORTNIGHTLY or WEEKLY. Monthly staff are always MONTHLY',
  joinedOn: 'Joining date, like 2026-09-01 (needed)',
  basicPay: 'Monthly staff: basic pay (needed for them)',
  houseRent: 'Monthly staff: house rent',
  conveyance: 'Monthly staff: conveyance',
  medical: 'Monthly staff: medical',
  dailyRate: 'Daily-wage staff: pay for one day (needed for them)',
  paymentMethod: 'CASH, BANK or WALLET. Empty means CASH',
  payBankName: 'Bank name',
  payAccountTitle: 'Account title',
  payIban: 'IBAN, 24 characters starting PK',
  walletProvider: 'JAZZCASH or EASYPAISA',
  walletNo: 'Wallet number, like 03001234567',
  eobiNo: 'EOBI number, if registered',
  pessiNo: 'PESSI number, if registered',
  taxApplies: 'yes or no. Empty means yes',
  taxableSoFar: 'Taxable pay already paid this tax year',
  taxSoFar: 'Income tax already taken this tax year',
  loanOwed: 'Loan still owed today',
  loanInstalment: 'Monthly loan instalment (needed if a loan is owed)',
  advanceOwed: 'Advance still owed today; taken from next pay',
  casualLeaveLeft: 'Casual leave days left this year',
  sickLeaveLeft: 'Sick leave days left this year',
  annualLeaveLeft: 'Annual leave days left this year',
};

interface CheckRow {
  line: number;
  name: string;
  errors: string[];
  notes: string[];
}

interface ServerResult {
  ok: boolean;
  saved: number;
  rows: Array<{ row: number; name: string; errors: string[]; notes: string[] }>;
}

function downloadTemplate() {
  const blob = new Blob([importTemplate()], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'people-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Go-live import, in three steps: choose a file, check it (nothing is saved;
 * every bad row says what is wrong), then import. The server saves every row
 * or none, so a half-imported staff list never has to be untangled.
 */
export function EmployeeImport({ onCancel, onDone }: { onCancel: () => void; onDone: (message: string) => void }) {
  const [fileName, setFileName] = useState('');
  const [sheet, setSheet] = useState<ImportSheet | null>(null);
  const [checked, setChecked] = useState<CheckRow[] | null>(null);
  const [ok, setOk] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Rows without a name cannot be sent at all; they are reported here.
  const sendable = (sheet?.rows ?? []).filter((r) => r.values.nameEn);
  const unnamed = (sheet?.rows ?? []).filter((r) => !r.values.nameEn);

  function body(dryRun: boolean) {
    return {
      dryRun,
      rows: sendable.map((r) => ({ ...r.values, workerType: r.values.workerType ?? '', joinedOn: r.values.joinedOn ?? '' })),
    };
  }

  const check = useMutation({
    mutationFn: () => api.post<ServerResult>('/api/v1/hr/employees/import', body(true)),
    onSuccess: (r) => {
      const rows: CheckRow[] = [
        ...unnamed.map((u) => ({ line: u.line, name: '—', errors: ['Name is required'], notes: [] })),
        ...r.rows.map((x) => ({ line: sendable[x.row - 1]?.line ?? x.row, name: x.name, errors: x.errors, notes: x.notes })),
      ].sort((a, b) => a.line - b.line);
      setChecked(rows);
      setOk(r.ok && unnamed.length === 0);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not check the file')),
  });

  const save = useMutation({
    mutationFn: () => api.post<ServerResult>('/api/v1/hr/employees/import', body(false)),
    onSuccess: (r) => {
      if (r.ok) onDone(`${r.saved} ${r.saved === 1 ? 'person' : 'people'} imported.`);
      else {
        setOk(false);
        setProblem('Something changed since the check. Check the file again.');
      }
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not import')),
  });

  async function choose(file: File | undefined) {
    setChecked(null);
    setOk(false);
    setProblem(null);
    setSheet(null);
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const s = readImportSheet(text);
    if (s.missing.length > 0) {
      setProblem(`The first line must name the columns. Missing: ${s.missing.join(', ')}. Download the template to see them.`);
      return;
    }
    if (s.rows.length === 0) return setProblem('The file has no people in it.');
    if (s.rows.length > MAX_ROWS) return setProblem(`At most ${MAX_ROWS} people can be imported at once. Split the file.`);
    setSheet(s);
  }

  const bad = checked?.filter((r) => r.errors.length > 0).length ?? 0;

  return (
    <FormPanel
      title="Import people from a CSV file"
      icon={<AppIcon kind="importFile" />}
      intro="For go-live: bring in everyone at once, with any loan or advance still owed, leave days left, and tax already taken this year. Save the sheet from Excel as CSV. Nothing is saved until every row is correct."
    >
      <div className="stack">
        <FormGroup title="1. Get the template" hint="The first line of your file must have these column names. Only nameEn, workerType and joinedOn must be filled for every person.">
          <div className="toolbar">
            <button type="button" className="btn btn--withicon" onClick={downloadTemplate}>
              <AppIcon kind="export" size={15} />
              Download template
            </button>
          </div>
          <details>
            <summary>What goes in each column</summary>
            <Table
              count={IMPORT_COLUMNS.length}
              empty=""
              head={
                <tr>
                  <th>Column</th>
                  <th>What to put</th>
                </tr>
              }
            >
              {IMPORT_COLUMNS.map((c) => (
                <tr key={c}>
                  <td className="mono">{c}</td>
                  <td>{COLUMN_HELP[c]}</td>
                </tr>
              ))}
            </Table>
          </details>
        </FormGroup>

        <FormGroup title="2. Choose your file and check it">
          <label className="field">
            <span className="field__label">CSV file</span>
            <input
              className="field__input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => void choose(e.target.files?.[0])}
            />
          </label>
          {sheet && (
            <p className="muted">
              {fileName}: {sheet.rows.length} {sheet.rows.length === 1 ? 'person' : 'people'} found.
              {sheet.unknown.length > 0 && ` These columns are not used and will be ignored: ${sheet.unknown.join(', ')}.`}
            </p>
          )}
          {sheet && (
            <div className="toolbar">
              <button type="button" className="btn" disabled={check.isPending} onClick={() => check.mutate()}>
                {check.isPending ? 'Checking…' : 'Check the file'}
              </button>
            </div>
          )}
        </FormGroup>

        {checked && (
          <FormGroup title="3. Look over the check">
            {ok ? (
              <OkNote message={`All ${checked.length} rows are correct. Nothing has been saved yet.`} />
            ) : (
              <ErrorNote message={`${bad} of ${checked.length} rows need fixing. Fix them in your file, then choose it again.`} />
            )}
            <Table
              count={checked.length}
              empty="Nothing to show."
              head={
                <tr>
                  <th className="num">Line</th>
                  <th>Name</th>
                  <th>Problems</th>
                  <th>Notes</th>
                </tr>
              }
            >
              {checked.map((r) => (
                <tr key={r.line} className={r.errors.length ? 'employees__badrow' : undefined}>
                  <td className="num">{r.line}</td>
                  <td>{r.name}</td>
                  <td>{r.errors.length ? r.errors.join('; ') : <span className="dim">None</span>}</td>
                  <td className="dim">{r.notes.join('; ') || '—'}</td>
                </tr>
              ))}
            </Table>
          </FormGroup>
        )}

        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="button" className="btn btn--primary" disabled={!ok || save.isPending} onClick={() => save.mutate()}>
            {save.isPending
              ? 'Importing…'
              : ok && checked
                ? `Import ${checked.length} ${checked.length === 1 ? 'person' : 'people'}`
                : 'Import people'}
          </button>
        </FormActions>
      </div>
    </FormPanel>
  );
}
